import { useState } from 'react';
import { Heading, Button, cn } from '@simplywallst/ui-core/next';
import EarningsReportOutline from '@simplywallst/ui-core/icons/EarningReportOutline';
import DividendOutline from '@simplywallst/ui-core/icons/DividendOutline';
import PriceUpOutline from '@simplywallst/ui-core/icons/PriceUpOutline';
import InsiderUpOutline from '@simplywallst/ui-core/icons/InsiderUpOutline';
import NewRiskOutline from '@simplywallst/ui-core/icons/NewRiskOutline';
import { useNewsFeed } from './hooks/useNewsFeed';
import { SidePanel } from './components/SidePanel';
import { useTracking } from 'react-tracking';
import loadable from '@loadable/component';
import { FeedCardSkeleton } from '@/components/FeedCard';
import { filterNewsByBriefName, getTotalPerBriefName } from './utils';
import Tooltip from '@/components/Tooltip';
import { CompanyNoteModalProvider } from '@/components/Note';

const FeedCard = loadable(() => import('@/components/FeedCard'));

type Props = {
  portfolioId: string;
};

export const NewsFeed = ({ portfolioId }: Props) => {
  const [showSidePanel, setShowSidePanel] = useState(false);

  const { entities, isFetching } = useNewsFeed(portfolioId);

  const filteredNews = filterNewsByBriefName(entities);
  const filteredKeys = Object.keys(filteredNews);

  const totalNewsPerType = getTotalPerBriefName(filteredNews);
  const hasUpdatedNews = Object.values(totalNewsPerType).some(
    (value) => value > 0
  );

  const { trackEvent } = useTracking();

  const handleClick = () => {
    setShowSidePanel(true);
    trackEvent({
      action: 'click',
      modifier: 'OpenNewsFeed',
    });
  };

  return (
    <>
      <div className="grid grid-rows-[50px_max-content] items-start gap-x2">
        <div className="grid grid-cols-[max-content_max-content] grid-rows-[max-content] items-baseline justify-between">
          <Heading as="h2" size="small">
            Updates <span className="text-tiny text-softer">Last 30d</span>
          </Heading>
          <Button
            variant="outline"
            size="medium"
            isDisabled={!hasUpdatedNews}
            onClick={handleClick}
          >
            All Updates
          </Button>
        </div>
        <div className="grid grid-cols-[repeat(5,56px)] justify-center gap-x1 md:grid-cols-[repeat(auto-fit,minmax(56px,1fr))]">
          <div className="flex">
            <Tooltip title="Earnings Report" light placement="top">
              <button
                className="flex w-full items-center justify-between rounded-x0_75 bg-p-blue-120/10 px-x1 py-x0_5 hover:bg-p-blue-120/20 disabled:cursor-default disabled:hover:bg-p-blue-120/10"
                disabled={!hasUpdatedNews}
                onClick={handleClick}
              >
                <EarningsReportOutline className="w-x3 !fill-brand-01" />
                <div
                  className={cn(
                    'text-sm',
                    totalNewsPerType.HasReportedEarnings > 0
                      ? 'opacity-100'
                      : 'opacity-30'
                  )}
                >
                  {totalNewsPerType.HasReportedEarnings || 0}
                </div>
              </button>
            </Tooltip>
          </div>
          <div className="flex">
            <Tooltip title="Upcoming Dividends" light placement="top">
              <button
                className="flex w-full items-center justify-between rounded-x0_75 bg-p-blue-120/10 px-x1 py-x0_5 hover:bg-p-blue-120/20 disabled:cursor-default disabled:hover:bg-p-blue-120/10"
                disabled={!hasUpdatedNews}
                onClick={handleClick}
              >
                <DividendOutline className="w-x3 !fill-brand-01" />
                <div
                  className={cn(
                    'text-sm',
                    totalNewsPerType.HasUpcomingDividend > 0
                      ? 'opacity-100'
                      : 'opacity-30'
                  )}
                >
                  {totalNewsPerType.HasUpcomingDividend || 0}
                </div>
              </button>
            </Tooltip>
          </div>
          <div className="flex">
            <Tooltip title="Analyst Price Target Update" light placement="top">
              <button
                className="flex w-full items-center justify-between rounded-x0_75 bg-p-blue-120/10 px-x1 py-x0_5 hover:bg-p-blue-120/20 disabled:cursor-default disabled:hover:bg-p-blue-120/10"
                disabled={!hasUpdatedNews}
                onClick={handleClick}
              >
                <PriceUpOutline className="w-x3 !fill-brand-01" />
                <div
                  className={cn(
                    'text-sm',
                    totalNewsPerType.HasPriceTargetChanged > 0
                      ? 'opacity-100'
                      : 'opacity-30'
                  )}
                >
                  {totalNewsPerType.HasPriceTargetChanged || 0}
                </div>
              </button>
            </Tooltip>
          </div>
          <div className="flex">
            <Tooltip title="Insider Trading" light>
              <button
                className="flex w-full items-center justify-between rounded-x0_75 bg-p-blue-120/10 px-x1 py-x0_5 hover:bg-p-blue-120/20 disabled:cursor-default disabled:hover:bg-p-blue-120/10"
                disabled={!hasUpdatedNews}
                onClick={handleClick}
              >
                <InsiderUpOutline className="w-x3 !fill-brand-01" />

                <div
                  className={cn(
                    'text-sm',
                    totalNewsPerType.HasRecentInsiderTransactions > 0
                      ? 'opacity-100'
                      : 'opacity-30'
                  )}
                >
                  {totalNewsPerType.HasRecentInsiderTransactions || 0}
                </div>
              </button>
            </Tooltip>
          </div>
          <div className="flex">
            <Tooltip title="New Risks" light>
              <button
                className="flex w-full items-center justify-between rounded-x0_75 bg-p-blue-120/10 px-x1 py-x0_5 hover:bg-p-blue-120/20 disabled:cursor-default disabled:hover:bg-p-blue-120/10"
                disabled={!hasUpdatedNews}
                onClick={handleClick}
              >
                <NewRiskOutline className="w-x3 !fill-brand-01" />
                <div
                  className={cn(
                    'text-sm',
                    totalNewsPerType.HasNewRisk > 0
                      ? 'opacity-100'
                      : 'opacity-30'
                  )}
                >
                  {totalNewsPerType.HasNewRisk || 0}
                </div>
              </button>
            </Tooltip>
          </div>
        </div>
      </div>
      {showSidePanel && (
        <CompanyNoteModalProvider>
          <SidePanel
            onClose={() => {
              setShowSidePanel(false);
              trackEvent({
                action: 'click',
                modifier: 'CloseNewsFeed',
              });
            }}
          >
            {isFetching ? (
              <>
                <FeedCardSkeleton />
                <FeedCardSkeleton />
                <FeedCardSkeleton />
              </>
            ) : (
              filteredKeys.map((n, i) => (
                <FeedCard
                  key={`feed-card-${n}`}
                  index={i}
                  company={filteredNews[n]}
                  data-cy-id="dashboard-feed-card"
                />
              ))
            )}
          </SidePanel>
        </CompanyNoteModalProvider>
      )}
    </>
  );
};
